'use client';

import React from 'react';
import { createPortal } from 'react-dom';

import CloseButton from '@/components/ui/Button/CloseButton';
import DropdownTriggerButton from '@/components/ui/Button/DropdownTriggerButton';
import { SPEC_OPTION_TITLE_BY_SLUG } from '@/features/search';

import { CATEGORY_OPTIONS } from '../hooks/useSpecialitiesFilter';
import {
  BottomSheet,
  CheckboxItem,
  DropdownFooter,
  RadioItem,
} from './SpecialitiesFilterComponents';
import RichText from '@/components/ui/RichText/RichText';

const desktopPanel =
  'absolute top-full left-0 mt-2 bg-white border border-gray-100 rounded-2xl z-50 p-5 shadow-[0_10px_40px_rgba(0,0,0,0.08)]';

const getSpecialityLabel = (slug: string): string =>
  SPEC_OPTION_TITLE_BY_SLUG[slug] ?? slug;

interface SpecialitiesFilterBarProps {
  query: string;
  onQueryChange: (v: string) => void;
  onSearch: () => void;
  openMenu: string | null;
  onOpenMenu: (key: string | null) => void;
  isMobile: boolean;
  selectedCategory: string;
  selectedSubSlugs: string[];
  subSpecialityOptions: { slug: string; title: string }[];
  subSpecialityTitleMap: Record<string, string>;
  subSpecsEnabled: boolean;
  isFiltered: boolean;
  onSelectCategory: (cat: string) => void;
  onToggleSubSlug: (slug: string) => void;
  onClearSubSlugs: () => void;
  onClearAll: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

const SpecialitiesFilterBar = ({
  query,
  onQueryChange,
  onSearch,
  openMenu,
  onOpenMenu,
  isMobile,
  selectedCategory,
  selectedSubSlugs,
  subSpecialityOptions,
  subSpecialityTitleMap,
  subSpecsEnabled,
  isFiltered,
  onSelectCategory,
  onToggleSubSlug,
  onClearSubSlugs,
  onClearAll,
  containerRef,
}: SpecialitiesFilterBarProps) => {
  const specialityPanel = (
    <>
      <div className="max-h-64 overflow-y-auto pr-1">
        <RadioItem
          value=""
          label="General"
          checked={selectedCategory === ''}
          onChange={() => onSelectCategory('')}
        />
        {CATEGORY_OPTIONS.map((option) => (
          <RadioItem
            key={option.slug}
            value={option.slug}
            label={option.title}
            checked={selectedCategory === option.slug}
            onChange={() => onSelectCategory(option.slug)}
          />
        ))}
      </div>
      <DropdownFooter
        onClear={() => {
          onSelectCategory('');
          onOpenMenu(null);
        }}
        onApply={() => onOpenMenu(null)}
      />
    </>
  );

  const subSpecialityPanel = (
    <>
      <div className="max-h-64 overflow-y-auto pr-1">
        {subSpecialityOptions.map((option) => (
          <CheckboxItem
            key={option.slug}
            slug={option.slug}
            title={option.title}
            checked={selectedSubSlugs.includes(option.slug)}
            onChange={() => onToggleSubSlug(option.slug)}
          />
        ))}
      </div>
      <DropdownFooter
        onClear={onClearSubSlugs}
        onApply={() => onOpenMenu(null)}
      />
    </>
  );

  return (
    <>
      {isMobile &&
        openMenu === 'spec' &&
        createPortal(
          <BottomSheet
            title="Select a Speciality"
            onClose={() => onOpenMenu(null)}
          >
            {specialityPanel}
          </BottomSheet>,
          document.body,
        )}

      {isMobile &&
        openMenu === 'subspec' &&
        subSpecsEnabled &&
        createPortal(
          <BottomSheet
            title="Select a Sub Speciality"
            onClose={() => onOpenMenu(null)}
          >
            {subSpecialityPanel}
          </BottomSheet>,
          document.body,
        )}

      <div
        ref={containerRef}
        className="mb-8 flex flex-col gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:flex-row sm:flex-wrap sm:items-center"
      >
        <div className="relative flex-1">
          <svg
            className="text-text-placeholder-dark absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            placeholder="Search by Speciality...."
            aria-label="Search by Speciality"
            className="placeholder-text-placeholder-dark w-full rounded-lg border border-slate-200 py-3 pr-4 pl-9 text-base font-bold text-slate-700 outline-none [&::-webkit-search-cancel-button]:cursor-pointer"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <RichText styles={{ p: 'text-sm font-semibold text-text-default' }}>
            Filter by
          </RichText>

          <div className="relative">
            <DropdownTriggerButton
              onClick={() => onOpenMenu(openMenu === 'spec' ? null : 'spec')}
              label={
                selectedCategory
                  ? getSpecialityLabel(selectedCategory)
                  : 'Speciality'
              }
              isActive={selectedCategory !== ''}
              minWidth="min-w-40"
              iconClassName={
                openMenu === 'spec' ? 'rotate-180 transition-transform' : ''
              }
            />
            {!isMobile && openMenu === 'spec' && (
              <div className={`${desktopPanel} shadow-brand w-72`}>
                {specialityPanel}
              </div>
            )}
          </div>

          {/* <div className="relative">
            <DropdownTriggerButton
              onClick={() =>
                subSpecsEnabled &&
                onOpenMenu(openMenu === 'subspec' ? null : 'subspec')
              }
              label={
                selectedSubSlugs.length > 0 && subSpecsEnabled
                  ? `Sub-Spec (${selectedSubSlugs.length})`
                  : 'Sub Speciality'
              }
              isActive={selectedSubSlugs.length > 0}
              disabled={!subSpecsEnabled}
              minWidth="min-w-[175px]"
              iconClassName={
                openMenu === 'subspec' ? 'rotate-180 transition-transform' : ''
              }
            />
            {!isMobile && openMenu === 'subspec' && subSpecsEnabled && (
              <div className={`${desktopPanel} w-72 shadow-brand`}>
                {subSpecialityPanel}
              </div>
            )}
          </div> */}

          <button
            onClick={onSearch}
            className="bg-theme-primary shrink-0 cursor-pointer rounded-lg px-6 py-3 text-sm font-normal text-white transition hover:bg-indigo-800"
          >
            Search
          </button>

          {isFiltered && (
            <button
              onClick={onClearAll}
              className="text-theme-danger-soft cursor-pointer text-sm font-semibold hover:underline"
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {(selectedCategory || selectedSubSlugs.length > 0) && (
        <div className="mb-6 flex flex-wrap gap-3">
          {selectedCategory && (
            <div className="bg-theme-success-subtle border-border-filter text-theme-action-primary flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium">
              {getSpecialityLabel(selectedCategory)}
              <CloseButton onClick={() => onSelectCategory('')} />
            </div>
          )}
          {selectedSubSlugs.map((slug) => (
            <div
              key={slug}
              className="bg-surface-tint-soft border-border-lavender-tint text-theme-action-primary flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium"
            >
              {subSpecialityTitleMap[slug] ?? slug}
              <CloseButton onClick={() => onToggleSubSlug(slug)} />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default React.memo(SpecialitiesFilterBar);
